/* eslint-disable no-console */
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import { ConfirmationModal, Container, flexColumn, TableAgGrid } from '@ubeya/shared-web/components';
import { isGridApiValid } from '@ubeya/shared-web/components/TableAgGrid/utils/isGridApiValid';
import { isMobileOnly } from 'react-device-detect';
import React, { useContext, useMemo, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import withPermissions from '@ubeya/shared-web/hoc/withPermissions';
import { TIMESHEET_GROUP_BY_OPTIONS_ENUM } from '@ubeya/shared/constants';
import useTimesheetPayrollFields from '@ubeya/shared/hooks/account/useTimesheetPayrollFields';
import { EmployeeCard, OSEmployeeCard } from 'shared/components';
import { TIMESHEET_TABLE_CONTEXT } from 'shared/constants/timeSheet';
import ActionRendererByRowType from './components/ActionRendererByRowType';
import ActionsColumnHeader from './components/ActionsColumnHeader';
import TimeSheetPDFExport from './TimeSheetPDFExport';
import { useTimeSheetGridColumns } from './hooks/useTimeSheetGridColumns';
import ShiftDetails from 'timeSheet/components/ShiftDetails/ShiftDetails';

const Wrapper = styled(Container)`
  ${flexColumn};
  flex: 1;

  ${({ isJobTimesheet }) =>
    isJobTimesheet &&
    css`
      padding: 0px;
    `}

  .user {
    width: 100%;
    position: relative;
  }
`;

const getRowId = ({ data: currentRowData, node }) => {
  if (!currentRowData) return node?.id ?? `row-${node?.rowIndex ?? 0}`;
  const { id, branchId, employee, date } = currentRowData;
  return id ? String(id) : `${String(branchId)}-${String(employee?.id)}-${String(date)}`;
};

const TimeSheetTableGrid = ({ timesheetProps, userPermissions, className }) => {
  const {
    data,
    isLoading,
    groupBy,
    refetchTimesheet,
    deleteTimeSheet,
    crossAccountUnbookEmployee,
    approveTimeSheet,
    approveAllTimeSheet,
    autofill,
    updateStartTime,
    updateEndTime,
    updateCheckInTime,
    updateCheckOutTime,
    deleteBreak,
    updateBreak,
    updateEmployeeRating,
    updateShiftDetails,
    updateTimesheetTaskField,
    createDailyShift,
    updateReviewOnEmployee,
    approveOSTimeSheets,
    autoFillOSTimeSheets,
    isBreakTimesDisplayed,
    showEmployeeSignoffColumn,
    showCheckInOutColumns = false,
    showDateColumn = false,
    showTotalTimeWithoutBreaksColumn = false,
    showShiftColumn = true,
    showTagsColumn = true,
    showTimesheetFields = false,
    isGroupedData = true,
    setShowPDF,
    extraTableActionIcon,
    extraTableActionHandler,
    extraTableActionIsLoading = false,
    extraTableActionTooltip,
    showExtraTableActionRenderer = false,
    tableContext,
    showLockedTimesheetColumn = false,
    showPayrollProcessFeature = false,
    clickableDate = false,
    showEmployeeRating = false,
    updateOSClientApproval,
    updateOSStartTime,
    updateOSEndTime,
    updateOSCheckInTime,
    updateOSCheckOutTime,
    updateOSBreaks,
    deleteOSBreak,
    updateOSEmployeeRating,
    updateOSReviewOnEmployee,
    updateOSShiftDetails,
    mappedSupplierBranches,
    amplitudeModuleName,
    persistenceKey = 'agGridTimesheetColumnState',
    timeRange,
    projectId,
    branchId,
    employeeIds
  } = timesheetProps;

  const wrapperClassName = className ?? timesheetProps?.className;
  const { hasActionPermission } = userPermissions || {};
  const { t } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const canEditPayrollFields = !!myPrivileges?.[privileges.EDIT_PAYROLL_FIELDS_ON_TIMESHEETS];
  const [shiftModal, setShiftModal] = useState();
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [isPromptOpen, setIsPromptOpen] = useState(false);

  const {
    fields: payrollFields,
    mapByTimesheetId,
    isLoading: isPayrollFieldsLoading,
    updateField: { mutateAsync: updatePayrollField }
  } = useTimesheetPayrollFields({
    timeRange,
    projectId,
    branchId,
    employeeIds,
    enabled: canEditPayrollFields
  });

  const rowData = useMemo(() => {
    const rows = isGroupedData ? data?.flatMap(({ subRows = [] }) => subRows) || [] : Array.isArray(data) ? data : [];

    if (!canEditPayrollFields) {
      return rows;
    }

    const rowsWithPayrollFields = rows.map((row) =>
      row?.timesheetId && mapByTimesheetId[row.timesheetId]
        ? { ...row, timesheetPayrollFields: mapByTimesheetId[row.timesheetId] }
        : row
    );

    const timesheetPayrollFieldsTotal = rowsWithPayrollFields.reduce((totals, row) => {
      (row?.timesheetPayrollFields?.fields || []).forEach((field) => {
        if (!field?.id) {
          return;
        }
        totals[field.id] = (totals[field.id] || 0) + (Number(field.value) || 0);
      });
      return totals;
    }, {});

    return rowsWithPayrollFields.map((row) => (row?.summarized ? { ...row, timesheetPayrollFieldsTotal } : row));
  }, [data, isGroupedData, canEditPayrollFields, mapByTimesheetId]);

  const handleUpdateShiftDetails = useCallback(
    (params) => (params.shift?.isExternal ? updateOSShiftDetails(params) : updateShiftDetails(params)),
    [updateOSShiftDetails, updateShiftDetails]
  );

  const columnDefs = useTimeSheetGridColumns({
    totalRows: rowData.length,
    mappedSupplierBranches,
    setShiftModal,
    approveTimeSheet,
    showDateColumn,
    updateStartTime,
    updateEndTime,
    updateCheckInTime,
    updateCheckOutTime,
    hasActionPermission,
    setSelectedEmployee,
    showCheckInOutColumns,
    showTotalTimeWithoutBreaksColumn,
    showShiftColumn,
    showEmployeeSignoffColumn,
    showTagsColumn,
    groupBy: isGroupedData ? groupBy : null,
    updateBreak,
    deleteBreak,
    isBreakTimesDisplayed,
    updateEmployeeRating,
    updateReviewOnEmployee,
    showTimesheetFields,
    setIsPromptOpen,
    tableContext,
    showLockedTimesheetColumn,
    clickableDate,
    showEmployeeRating,
    updateOSClientApproval,
    updateOSStartTime,
    updateOSEndTime,
    updateOSCheckInTime,
    updateOSCheckOutTime,
    updateOSBreaks,
    deleteOSBreak,
    updateOSEmployeeRating,
    updateOSReviewOnEmployee,
    refetchTimesheet,
    showPayrollProcessFeature,
    updateTimesheetTaskField,
    amplitudeModuleName,
    payrollFields,
    updatePayrollField
  });

  const actionRendererParams = useMemo(
    () => ({
      approveAllTimeSheet,
      autofill,
      groupBy,
      hasActionPermission,
      setShiftModal,
      deleteTimeSheet,
      crossAccountUnbookEmployee,
      createDailyShift,
      refetchTimesheet,
      mappedSupplierBranches,
      approveOSTimeSheets,
      autoFillOSTimeSheets,
      amplitudeModuleName
    }),
    [
      amplitudeModuleName,
      approveAllTimeSheet,
      autofill,
      groupBy,
      hasActionPermission,
      deleteTimeSheet,
      crossAccountUnbookEmployee,
      createDailyShift,
      refetchTimesheet,
      mappedSupplierBranches,
      approveOSTimeSheets,
      autoFillOSTimeSheets
    ]
  );

  const changeGroupByCallback = useCallback((api, newGroupBy) => {
    if (!isGridApiValid(api)) return;

    try {
      const actionsColumn = api.getColumn('actions');
      if (!actionsColumn) return;

      const newWidth = newGroupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.PROJECT ? 90 : 50;
      const currentWidth = actionsColumn.getActualWidth();

      if (currentWidth !== newWidth) {
        api.setColumnWidth('actions', newWidth, true);
      }
    } catch (error) {
      console.warn('TimeSheetTableGrid: Error in changeGroupByCallback', error);
    }
  }, []);

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: '',
      width: 1,
      maxWidth: 1,
      minWidth: 1,
      suppressSizeToFit: true,
      resizable: false,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: () => null
      }
    }),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultExtendsRows = useMemo(() => ({ 0: data?.length <= 2 }), [data?.length, groupBy]);

  const actionColumnOptions = useMemo(
    () =>
      showExtraTableActionRenderer && extraTableActionIcon && extraTableActionHandler
        ? {
            headerComponent: ActionsColumnHeader,
            headerComponentParams: {
              tooltip: extraTableActionTooltip,
              isLoading: extraTableActionIsLoading,
              icon: extraTableActionIcon,
              onClick: extraTableActionHandler
            }
          }
        : {},
    [
      showExtraTableActionRenderer,
      extraTableActionIcon,
      extraTableActionHandler,
      extraTableActionTooltip,
      extraTableActionIsLoading
    ]
  );

  return (
    <Wrapper
      className={wrapperClassName}
      $isMobileOnly={isMobileOnly}
      isJobTimesheet={tableContext === TIMESHEET_TABLE_CONTEXT.EMPLOYEE_JOB_TAB}>
      <TableAgGrid
        rowData={rowData}
        columnDefs={columnDefs}
        groupBy={isGroupedData ? groupBy : null}
        defaultSortColumn={isGroupedData ? 'name' : null}
        changeGroupByCallback={changeGroupByCallback}
        clickableGroupRowClasses={['user', 'project-name-clickable', 'row-actions-wrapper']}
        autoGroupColumnDef={autoGroupColumnDef}
        persistenceKey={persistenceKey}
        actionRenderer={ActionRendererByRowType}
        actionRendererParams={actionRendererParams}
        actionColumnOptions={actionColumnOptions}
        isLoading={isLoading || isPayrollFieldsLoading}
        defaultExtendsRows={defaultExtendsRows}
        getRowId={getRowId}
      />

      {shiftModal && (
        <ShiftDetails
          shift={shiftModal}
          onClose={() => setShiftModal()}
          updateShiftDetails={handleUpdateShiftDetails}
          readOnly={!hasActionPermission}
        />
      )}
      {selectedEmployee && !selectedEmployee.isExternal && (
        <EmployeeCard defaultData={selectedEmployee} onClose={() => setSelectedEmployee()} />
      )}
      {selectedEmployee && selectedEmployee.isExternal && (
        <OSEmployeeCard
          clientProjectId={selectedEmployee.clientProjectId}
          defaultData={selectedEmployee}
          onClose={() => setSelectedEmployee()}
          refetchPayroll
        />
      )}
      {isPromptOpen && (
        <ConfirmationModal
          onClose={() => setIsPromptOpen(false)}
          title={t('areYouSureYouEnteredTheCorrectTime')}
          body={t('theShiftDurationWillBeHours', { duration: isPromptOpen.duration })}
          onConfirm={() => {
            isPromptOpen.onConfirm?.();
            setIsPromptOpen(false);
          }}
          confirmText={t('ok')}
          showCancelButton
        />
      )}
      {setShowPDF && <TimeSheetPDFExport timesheetProps={timesheetProps} userPermissions={userPermissions} />}
    </Wrapper>
  );
};

export default withPermissions(TimeSheetTableGrid, {
  action: {
    accountSettings: {
      notAllowed: ['isAccountNetworkAccessSupplier']
    }
  }
});
